import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Package,
  PenLine,
  Palette,
  Search,
  BarChart2,
  ArrowRight,
  Upload,
  Image,
  FileText,
  Tag,
  CheckCircle,
  BarChart,
  Globe,
  Info,
  Play
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { useEffect, useState } from "react";
import GetStartedDialog from "../dialogs/GetStartedDialog";
import { compareDashboardUserState } from "../store/userSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { updateCurrentUser, updateMiqCollection } from "wasp/client/operations";
import { createShopifyToInternalTableUrl } from "../admin/dashboards/urls";
import { useQuery, getAllCollectionsOfUser } from "wasp/client/operations";
import { GetAllCollectionsOfUserOutput } from "../admin/dashboards/operations";
import { Progress } from "../components/ui/progress";
import { useImportShopify } from "../hooks/useImportShopify";
import { useDispatch } from "react-redux";
import { setIsGetStartedDialogOpen } from "../store/flowSlice";
import { useJobProgress } from "../hooks/useJobProgress";
import { ImportJobStatusResponse } from "../admin/dashboards/types";
import ContentImpactStatic from "./ContentImpactStatic";
import * as Sentry from "@sentry/react";
import { api } from "../services/api";
import { getInitialScoreStateless } from "../store/score/getInitialScore";
import { analytics } from "../analytics";
import { useTranslation, TranslationKey } from "../i18n/hooks";

const featureDetails = [
  {
    icon: BarChart,
    title: "detailed_content_analysis",
    description: "see_where_content_stands",
    details: {
      title: "content_analysis_features",
      description: "advanced_content_analysis",
      sections: [
        {
          title: "quality_metrics",
          points: [
            "title_optimization_score",
            "description_completeness_analysis",
            "image_quality_assessment",
            "product_specification_completeness"
          ],
        },
        {
          title: "improvement_recommendations",
          points: [
            "actionable_suggestions",
            "content_gap_analysis",
            "keyword_density_recommendations",
            "best_practices_customized"
          ],
        },
      ],
    },
  },
  {
    icon: Package,
    title: "ai_powered_suggestions",
    description: "get_tailored_recommendations",
    details: {
      title: "ai_powered_enhancement_tools",
      description: "leverage_advanced_ai",
      sections: [
        {
          title: "smart_content_generation",
          points: [
            "ai_generated_product_descriptions",
            "automatic_feature_extraction",
            "seo_optimized_title_suggestions",
            "category_specific_templates"
          ],
        },
        {
          title: "conversion_optimization",
          points: [
            "purchase_trigger_identification",
            "key_feature_emphasis",
            "clarity_persuasion_scoring",
            "mobile_optimization_suggestions"
          ],
        },
      ],
    },
  },
  {
    icon: Globe,
    title: "competitive_insights",
    description: "see_how_you_compare",
    details: {
      title: "competitive_intelligence_features",
      description: "understand_market_position",
      sections: [
        {
          title: "market_analysis",
          points: [
            "category_specific_benchmarks",
            "competitor_content_strategy",
            "market_trend_identification",
            "best_in_class_comparison"
          ],
        },
        {
          title: "performance_metrics",
          points: [
            "content_quality_comparison",
            "category_ranking_analysis",
            "conversion_rate_benchmarking",
            "customer_engagement_metrics"
          ],
        },
      ],
    },
  },
];

type Feature = {
  icon: any;
  title: string;
  description: string;
  details: {
    title: string;
    description: string;
    sections: {
      title: string;
      points: string[];
    }[];
  };
};

const FeatureInfoDialog = ({
  isOpen,
  onClose,
  feature,
}: {
  isOpen: boolean;
  onClose: () => void;
  feature: Feature;
}) => {
  const { t } = useTranslation();
  
  if (!feature) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onClose}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t(`dashboard.${feature.details.title}` as any as TranslationKey)}</DialogTitle>
          <DialogDescription>{t(`dashboard.${feature.details.description}` as any as TranslationKey)}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <div className="space-y-6">
            {feature.details.sections.map((section, index) => (
              <div
                key={index}
                className="space-y-3"
              >
                <h3 className="text-lg font-semibold text-gray-900">
                  {t(`dashboard.${section.title}` as any as TranslationKey)}
                </h3>
                <ul className="space-y-2">
                  {section.points.map((point, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2"
                    >
                      <div className="mt-1.5">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                      </div>
                      <span className="text-gray-600">{t(`dashboard.${point}` as any as TranslationKey)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('common.close' as any as TranslationKey)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FirstTimeUser = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {currentUser, currentCollection } = useSelector(
    (state: RootState) => state.user
  );
  const dashboardUserState = useSelector((state: RootState) => state.user.currentCollection.dashboardUserState);
  const { importShopify } = useImportShopify();
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const handleConnectShopify = () => {
    if (compareDashboardUserState(dashboardUserState, "initial") === 0) {
      dispatch(setIsGetStartedDialogOpen(true));
    }
  };

  const handleInfoClick = (feature: Feature) => {
    setSelectedFeature(feature);
    setIsDialogOpen(true);
  };

  const handleStartAnalysis = async () => {
    analytics.sendAnalysisButtonClickedEvent({
      email: currentUser?.email ?? "unknown",
      entryPoint: "dashboard",
    });
    if (compareDashboardUserState(dashboardUserState, "initial") === 0) {
      dispatch(setIsGetStartedDialogOpen(true));
    } else if (
      compareDashboardUserState(dashboardUserState, "connectedShopify") === 0
    ) {
      dispatch(setIsGetStartedDialogOpen(true));
      await importShopify();
    }
  };

  const {activeGenericJobs} = useSelector((state: RootState) => state.jobs); 
  const getImportJobId = () => {
    if(currentCollection?.collection?.miqImportMethod === "excel"){
      const job = activeGenericJobs["import_excel"];
      return job?.trackingId;
    }else if(currentCollection?.collection?.miqImportMethod === "shopify"){
      return currentCollection?.collection?.miqShopifyJobId;
    }
  }

  return (
    <div className="space-y-6">
      <ContentImpactStatic
        jobId={getImportJobId()}
        activeCollectionId={currentCollection?.collection?.id}
        dashboardUserState={dashboardUserState}
        handleStartAnalysis={handleStartAnalysis}
      />

      <div className="grid gap-6 md:grid-cols-2">
        {compareDashboardUserState(dashboardUserState, "importedProducts") <
          0 && (
          <Card className="transition-all duration-300 hover:shadow-md">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900">
                {t("dashboard.quick_start" as any as TranslationKey)}
              </CardTitle>
              <CardDescription className="text-gray-500">
                {t("dashboard.choose_import_method" as any as TranslationKey)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {compareDashboardUserState(dashboardUserState, "initial") ===
                0 && (
                <Button
                  onClick={handleConnectShopify}
                  variant="outline"
                  className="w-full mb-2 bg-white hover:bg-gray-50 text-gray-900 group relative overflow-hidden transition-all duration-300 border border-gray-200"
                >
                  <div className="flex items-center justify-between w-full px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 transition-transform group-hover:scale-110" />
                      <span className="font-medium">
                        {t(
                          "dashboard.connect_shopify" as any as TranslationKey
                        )}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Button>
              )}

              {compareDashboardUserState(dashboardUserState, "initial") > 0 && (
                <Button
                  onClick={async () => {
                    await importShopify();
                  }}
                  disabled={
                    compareDashboardUserState(
                      dashboardUserState,
                      "importInProgress"
                    ) === 0
                  }
                  variant="outline"
                  className="w-full mb-2 bg-white hover:bg-gray-50 text-gray-900 group relative overflow-hidden transition-all duration-300 border border-gray-200 disabled:opacity-70 disabled:hover:bg-white"
                >
                  <div className="flex items-center justify-between w-full px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 transition-transform group-hover:scale-110" />
                      <span className="font-medium">
                        {t(
                          "dashboard.import_shopify_data" as any as TranslationKey
                        )}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Button>
              )}

              {["WooCommerce", "Etsy", "Temu"].map((store) => (
                <Button
                  key={store}
                  disabled
                  variant="outline"
                  className="w-full group relative transition-all duration-300 mb-2 last:mb-0 border-gray-100"
                >
                  <div className="flex items-center justify-between w-full px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-gray-300" />
                      <span className="font-medium text-gray-300">
                        {t("dashboard.connect_store" as any as TranslationKey, {
                          store,
                        })}
                      </span>
                    </div>
                    <span className="text-xs bg-gray-50 text-gray-400 px-3 py-1 rounded-full">
                      {t("common.soon" as any as TranslationKey)}
                    </span>
                  </div>
                </Button>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-semibold">
              {t("dashboard.what_youll_get" as any as TranslationKey)}
            </CardTitle>
            <CardDescription>
              {t("dashboard.after_analyzing" as any as TranslationKey)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {featureDetails.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleInfoClick(item)}
                  className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <item.icon className="w-5 h-5 text-green-500" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-medium text-gray-900">
                      {t(`dashboard.${item.title}` as any as TranslationKey)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t(
                        `dashboard.${item.description}` as any as TranslationKey
                      )}
                    </p>
                  </div>
                  <div className="flex-shrink-0 p-1.5 hover:bg-gray-100 rounded-full transition-colors">
                    <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <FeatureInfoDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        feature={selectedFeature || featureDetails[0]}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            {t("dashboard.platform_demo" as any as TranslationKey) || "Platform Demo"}
          </CardTitle>
          <CardDescription>
            {t("dashboard.watch_platform_overview" as any as TranslationKey) || "Watch a quick overview of our platform"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!showVideo ? (
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              <Button 
                variant="outline" 
                onClick={() => setShowVideo(true)}
                className="flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                {t("dashboard.watch_demo" as any as TranslationKey) || "Watch Demo"}
              </Button>
            </div>
          ) : (
            <div style={{ position: "relative", overflow: "hidden", aspectRatio: "1920/1080" }}>
              <iframe
                src="https://share.synthesia.io/embeds/videos/339f6473-96a2-4ead-b151-ba998b647ad2"
                loading="lazy"
                title="Synthesia video player - Content Analysis Dashboard Overview"
                allowFullScreen
                allow="encrypted-media; fullscreen;"
                style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0, border: "none", padding: 0, margin: 0, overflow: "hidden" }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FirstTimeUser;
